<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue';

interface Props {
  fullWidth?: boolean;
  label?: string;
  loading: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  fullWidth: false,
  label: 'Unlock',
});

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void;
}>();

const showSpinner = ref(false);
const ripple = ref<{ id: number; x: number; y: number } | null>(null);
let spinnerTimeoutId: number | null = null;
let rippleTimeoutId: number | null = null;
let nextRippleId = 0;

function clearSpinnerTimer(): void {
  if (spinnerTimeoutId !== null) {
    window.clearTimeout(spinnerTimeoutId);
    spinnerTimeoutId = null;
  }
}

function clearRippleTimer(): void {
  if (rippleTimeoutId !== null) {
    window.clearTimeout(rippleTimeoutId);
    rippleTimeoutId = null;
  }
}

function handleClick(event: MouseEvent): void {
  const button = event.currentTarget as HTMLButtonElement;
  const bounds = button.getBoundingClientRect();

  clearRippleTimer();
  ripple.value = {
    id: nextRippleId++,
    x: event.clientX - bounds.left,
    y: event.clientY - bounds.top,
  };
  rippleTimeoutId = window.setTimeout(() => {
    ripple.value = null;
    rippleTimeoutId = null;
  }, 700);

  emit('click', event);
}

watch(
  () => props.loading,
  (loading) => {
    clearSpinnerTimer();
    if (!loading) {
      showSpinner.value = false;
      return;
    }

    spinnerTimeoutId = window.setTimeout(() => {
      showSpinner.value = true;
      spinnerTimeoutId = null;
    }, 150);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  clearSpinnerTimer();
  clearRippleTimer();
});
</script>

<template>
  <button
    type="button"
    data-test-id="enrichment-unlock-button"
    class="relative inline-flex h-9 shrink-0 items-center justify-center overflow-hidden rounded bg-shade-880 px-3 text-xs font-bold text-white transition-colors duration-150 ease-out enabled:hover:bg-shade-860 motion-reduce:transition-none"
    :class="{
      'bg-enrichment-button-pressed': loading,
      'w-full': fullWidth,
      'w-[92px]': !fullWidth,
    }"
    :disabled="loading"
    @click.stop.prevent="handleClick"
  >
    <span
      v-if="ripple"
      :key="ripple.id"
      data-test-id="enrichment-unlock-ripple"
      class="enrichment-unlock-button__ripple pointer-events-none absolute -ml-[26px] -mt-[26px] h-[52px] w-[52px] rounded-full border-2 border-highlight-300 motion-reduce:animate-none"
      :style="{ left: `${ripple.x}px`, top: `${ripple.y}px` }"
      aria-hidden="true"
    />

    <span
      class="inline-flex items-center justify-center gap-2 transition-opacity duration-150 ease-out motion-reduce:transition-none"
      :class="{ 'opacity-0': showSpinner }"
    >
      <slot name="icon" />
      {{ label }}
    </span>
    <span
      v-if="showSpinner"
      data-test-id="enrichment-unlock-spinner"
      class="enrichment-unlock-button__spinner absolute left-1/2 top-1/2 h-4 w-4 rounded-full motion-reduce:animate-none"
      aria-hidden="true"
    />
  </button>
</template>

<style scoped lang="postcss">
.enrichment-unlock-button__spinner {
  background: conic-gradient(currentColor 0deg 108deg, transparent 108deg 360deg);
  mask: radial-gradient(farthest-side, transparent calc(100% - 2px), black 0);
  transform: translate(-50%, -50%);
  animation: enrichment-unlock-spin 666.67ms linear infinite;
}

.enrichment-unlock-button__ripple {
  animation: enrichment-unlock-ripple 700ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

@keyframes enrichment-unlock-spin {
  from {
    transform: translate(-50%, -50%) rotate(0);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

@keyframes enrichment-unlock-ripple {
  from {
    opacity: 0.8;
    transform: scale(0);
  }
  to {
    opacity: 0;
    transform: scale(1);
  }
}
</style>
